import { useEffect, useMemo, useState } from "react";
import PostCard from "./PostCard";
import CategoryPills from "./Categorypills";
import AdShortcode from "./AdShortcode";

function useGridColumns() {
  const getCols = () => {
    if (typeof window === "undefined") return 3;
    if (window.matchMedia("(min-width: 1024px)").matches) return 3; // lg
    if (window.matchMedia("(min-width: 768px)").matches) return 2;  // md
    return 1;
  };

  const [cols, setCols] = useState(getCols);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mqLg = window.matchMedia("(min-width: 1024px)");
    const mqMd = window.matchMedia("(min-width: 768px)");

    const onChange = () => setCols(getCols());
    mqLg.addEventListener?.("change", onChange);
    mqMd.addEventListener?.("change", onChange);

    return () => {
      mqLg.removeEventListener?.("change", onChange);
      mqMd.removeEventListener?.("change", onChange);
    };
  }, []);

  return cols;
}

function PostGrid({
  posts,
  loading,
  categoryLabel,
  categorySlug,
  categoryLink,
  showCategoryPills = true,
  showCategoryTag = true,
  mixedCategories = false,
  darkMode,
  showInlineAd = false,
  inlineAdEveryRows = 2,
  inlineAdShortcode = "[trw_ad_slider]",
}) {
  const [visibleCount, setVisibleCount] = useState(posts.length);
  const cols = useGridColumns();

  const inlineAdEveryItems = useMemo(() => {
    const rows = Math.max(1, Number(inlineAdEveryRows) || 1);
    return cols * rows;
  }, [cols, inlineAdEveryRows]);

  useEffect(() => {
    setVisibleCount(posts.length);
  }, [posts]);

  if (loading) {
    return (
      <>
        {showCategoryPills ? <CategoryPills darkMode={darkMode} /> : null}
        <div className="mt-5 grid grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <article
              key={`skeleton-${index}`}
              className="relative flex h-full flex-col overflow-hidden rounded-[18px] bg-[#f8f9fa] p-[18px]"
              aria-hidden="true"
            >
              <div className="relative w-full aspect-[1.38] overflow-hidden rounded-[12px] bg-white">
                {showCategoryTag ? (
                  <span
                    className="absolute left-[14px] top-0 z-[2] inline-flex min-h-[28px] items-center justify-center bg-[#d02525] px-3 text-[12px] font-normal uppercase leading-[12px] text-white"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    {categoryLabel || "Loading"}
                  </span>
                ) : null}
                <div className="absolute right-[8px] top-[8px] z-[1] h-[44px] w-[44px] rounded-full border border-[#ccc] bg-white" />
                <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[rgba(122,104,234,0.12)] via-[rgba(223,47,42,0.08)] to-[#eef1f8]" />
              </div>
              <div className="flex flex-1 flex-col gap-4 pt-4">
                <div className="h-[56px] w-full animate-pulse rounded-md bg-[#eceef7]" />
                <div className="h-[72px] w-full animate-pulse rounded-md bg-[#eceef7]" />
                <div className="ml-auto h-[50px] w-[160px] animate-pulse rounded-full bg-[#eceef7]" />
              </div>
            </article>
          ))}
        </div>
      </>
    );
  }

  if (!posts.length) {
    return (
      <p className="mt-6 rounded-[24px] bg-[#f7f8fc] p-6 text-[16px] font-normal leading-6 text-[rgb(91,95,98)]">
        No posts found for this page.
      </p>
    );
  }

  return (
    <>
      {showCategoryPills ? <CategoryPills darkMode={darkMode} /> : null}
      <div className="mt-5 grid grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3">
        {posts.slice(0, visibleCount).flatMap((post, index) => {
          const items = [
            <PostCard
              key={post.__bookmarkId || post.id || post.slug || post.link}
              post={post}
              index={index}
              categoryLabel={categoryLabel}
              categorySlug={categorySlug}
              categoryLink={categoryLink}
              showCategoryTag={showCategoryTag}
              mixedCategories={mixedCategories}
            />,
          ];

          const isBoundary = inlineAdEveryItems > 0 && ((index + 1) % inlineAdEveryItems === 0);
          const hasMoreAfter = (index + 1) < visibleCount;
          if (showInlineAd && isBoundary && hasMoreAfter) {
            items.push(
              <div key={`trw-inline-ad-${index}`} className="md:col-span-2 lg:col-span-3">
                <AdShortcode shortcode={inlineAdShortcode} />
              </div>
            );
          }

          return items;
        })}
      </div>
    </>
  );
}

export default PostGrid;
